import { api } from '../lib/api'

// Portal do cliente — lado da equipe (src/routes/portalAdmin.ts e as publicações).

export interface PortalUserRow {
  id: string
  name: string
  email: string
  active: boolean
  weeklyEmail: boolean
  lastSeenAt: string | null
  createdAt: string
}

export interface PortalPublicationRow {
  id: string
  kind: 'relatorio' | 'achado'
  title: string
  periodLabel: string | null
  publishedAt: string
  hiddenAt: string | null
}

export interface PortalEventRow {
  id: string
  type: string
  text: string | null
  createdAt: string
  userName: string | null
}

export type LeadTracking = 'campanha' | 'geral' | 'nenhum'

export interface PortalAdminState {
  configured: boolean
  portalUrl: string | null
  active: boolean
  enabled: boolean
  canManage: boolean
  users: PortalUserRow[]
  publications: PortalPublicationRow[]
  openQuestions: number
  openRequests: number
  /** O que perguntar ao dono sobre os contatos toda segunda. level null = ainda não sabemos. */
  leadTracking: { level: LeadTracking | null; at: string | null; by: 'dono' | 'gerente' | 'automatico' | null }
  events: PortalEventRow[]
}

export interface TeamRequest {
  id: string
  text: string
  status: 'aberto' | 'respondido' | 'feito'
  answer: string | null
  answeredAt: string | null
  createdAt: string
  userName: string | null
  answeredByName: string | null
  client: { id: string; name: string }
}

export interface ChangeAlert {
  clientId: string
  clientName: string
  day: string
  reviewedAt: string | null
  reviewedBy: string | null
  note: string | null
  changes: Array<{ id: string; text: string; who: string | null }>
}

export interface TeamDossierItem {
  id: string
  content: string
  section: string
  visibility: 'shown' | 'internal' | 'unchecked'
  fromClient: boolean
  clientReview: 'ok' | 'wrong' | null
  clientReviewedAt: string | null
}

export interface WorkLogTeam {
  monitoredPages: number
  weeks: Array<{
    weekStart: string
    weekEnd: string
    analyses: string[]
    actions: Array<{
      id: string
      kind: 'optimization' | 'derived'
      day: string
      text: string
      custom: boolean
      original: string
      hidden: boolean
      otherAction: boolean
    }>
    changes: Array<{ kind: string; count: number; text: string }>
  }>
}

export interface TeamChat {
  id: string
  createdAt: string
  updatedAt: string
  user: { name: string }
  messages: Array<{
    id: string
    role: 'user' | 'assistant'
    content: string
    meta: { actions?: Array<{ type: 'pedido' | 'fato'; text: string }> } | null
    createdAt: string
  }>
}

export interface PortalNewsItem {
  id: string
  type: string
  text: string | null
  createdAt: string
  userName: string | null
  client: { id: string; name: string }
}

const base = (clientId: string) => `/clients/${clientId}/portal`

export const portalAdminApi = {
  get: (clientId: string) => api.get<PortalAdminState>(base(clientId)),
  setEnabled: (clientId: string, enabled: boolean) => api.put<{ enabled: boolean }>(`${base(clientId)}/enabled`, { enabled }),
  setLeadTracking: (clientId: string, level: LeadTracking | null) =>
    api.put<{ ok: true }>(`${base(clientId)}/lead-tracking`, { level }),
  addUser: (clientId: string, body: { name: string; email: string; invite: boolean }) =>
    api.post<{ user: PortalUserRow; invite: 'sent' | 'not_enabled' | 'not_configured' | 'failed'; error: string | null }>(
      `${base(clientId)}/users`,
      body
    ),
  updateUser: (clientId: string, userId: string, body: Partial<Pick<PortalUserRow, 'name' | 'active' | 'weeklyEmail'>>) =>
    api.put<{ ok: true }>(`${base(clientId)}/users/${userId}`, body),
  sendLink: (clientId: string, userId: string) => api.post<{ ok: true }>(`${base(clientId)}/users/${userId}/link`),
  revoke: (clientId: string, userId: string) => api.post<{ ok: true }>(`${base(clientId)}/users/${userId}/revoke`),
  removeUser: (clientId: string, userId: string) => api.del<{ ok: true }>(`${base(clientId)}/users/${userId}`),
  work: (clientId: string) => api.get<{ work: WorkLogTeam }>(`${base(clientId)}/work`),
  updateWork: (clientId: string, kind: string, actionId: string, body: { clientText?: string | null; clientHidden?: boolean }) =>
    api.put<{ ok: true }>(`${base(clientId)}/work/${kind}/${actionId}`, body),
  setPublicationHidden: (clientId: string, pubId: string, hidden: boolean) =>
    api.put<{ ok: true }>(`${base(clientId)}/publications/${pubId}`, { hidden }),
  writeTexts: (clientId: string) => api.post<{ written: number }>(`${base(clientId)}/work/texts`),
  dossier: (clientId: string) => api.get<{ items: TeamDossierItem[] }>(`${base(clientId)}/dossier`),
  setInternal: (clientId: string, insightId: string, internal: boolean) =>
    api.put<{ ok: true }>(`${base(clientId)}/dossier/${insightId}`, { internal }),
  classifyDossier: (clientId: string) => api.post<{ classified: number }>(`${base(clientId)}/dossier/classify`),
  chats: (clientId: string) => api.get<{ conversations: TeamChat[] }>(`${base(clientId)}/chats`),
  clientRequests: (clientId: string) => api.get<{ requests: TeamRequest[] }>(`${base(clientId)}/requests`),

  requests: () => api.get<{ requests: TeamRequest[] }>('/intelligence/portal-requests'),
  answerRequest: (id: string, body: { answer?: string; status?: TeamRequest['status'] }) =>
    api.put<{ ok: true }>(`/intelligence/portal-requests/${id}`, body),
  alerts: () => api.get<{ alerts: ChangeAlert[] }>('/intelligence/change-alerts'),
  resolveAlert: (clientId: string, day: string) => api.put<{ ok: true }>('/intelligence/change-alerts/resolve', { clientId, day }),

  publishBiweekly: (reportId: string, title: string, content: string) =>
    api.post<{ publication: { id: string } }>(`/reports/${reportId}/portal-publish`, { title, content }),
  findingDraft: (investigationId: string) =>
    api.post<{ title: string; content: string }>(`/investigations/${investigationId}/client-draft`),
  publishFinding: (investigationId: string, title: string, content: string) =>
    api.post<{ publication: { id: string } }>(`/investigations/${investigationId}/publish`, { title, content }),
  news: () => api.get<{ news: PortalNewsItem[] }>('/intelligence/portal-news'),
}
